import { useState } from "react";
import { Mail, Phone, Calendar, X, MessageCircle, Send, MapPin, Clock } from 'lucide-react';

export default function Contact() {
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    communityType: '',
    message: ''
  });

  const togglePopup = () => {
    setIsPopupVisible(!isPopupVisible);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Form submitted:', formData);
    togglePopup();
  };

  return (
    <>
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white" id="contactUs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Contact Info */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-purple-600/10 text-purple-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <MessageCircle className="w-4 h-4" />
                Ready to Get Started?
              </div>
              
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Start Building Your 
                <span className="block bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                  Dream Community
                </span>
                Today
              </h2>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Join thousands of community builders who trust Communiticate to create engaging, 
                thriving communities that bring people together.
              </p>

              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4 text-gray-600">
                  <div className="w-12 h-12 bg-purple-600/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Free Setup & Onboarding</h4>
                    <p className="text-sm">Get personalized help setting up your community</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 text-gray-600">
                  <div className="w-12 h-12 bg-purple-600/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Launch in Minutes</h4>
                    <p className="text-sm">Start building your community in less than 5 minutes</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 text-gray-600">
                  <div className="w-12 h-12 bg-purple-600/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Dedicated Support</h4>
                    <p className="text-sm">Get expert guidance and 24/7 community support</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={togglePopup}
                  className="group bg-gradient-to-r from-purple-600 to-blue-600 hover:from-blue-600 hover:to-purple-600 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-lg flex items-center justify-center gap-2"
                >
                  <Send className="w-5 h-5" />
                  Start Your Community Free
                </button>
                
                <a
                  href="mailto:hello@communiticate.com"
                  className="group bg-white hover:bg-gray-50 text-gray-800 border-2 border-gray-200 hover:border-purple-600 px-8 py-4 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <Mail className="w-5 h-5 group-hover:text-purple-600" />
                  Get in Touch
                </a>
              </div>

              {/* Contact Info */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-purple-600" />
                    <span>hello@communiticate.com</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-purple-600" />
                    <span>Global Community Platform</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Visual Element */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl blur-2xl opacity-20"></div>
              <div className="relative bg-white rounded-3xl p-8 shadow-2xl border border-gray-100">
                <div className="text-center space-y-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl flex items-center justify-center mx-auto">
                    <Send className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Join the Community Revolution</h3>
                  <p className="text-gray-600">Transform how people connect and collaborate with powerful community building tools.</p>
                  
                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-6 pt-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600">1000+</div>
                      <div className="text-sm text-gray-600">Communities</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600">50K+</div>
                      <div className="text-sm text-gray-600">Active Members</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600">98%</div>
                      <div className="text-sm text-gray-600">Satisfaction</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600">24/7</div>
                      <div className="text-sm text-gray-600">Support</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Contact Form Popup */}
      {isPopupVisible && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold">Start Your Community Journey</h3>
                <button
                  onClick={togglePopup}
                  className="p-2 rounded-full hover:bg-white/20 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-white/90 mt-2">Tell us about your community vision and we'll help you get started!</p>
            </div>
            
            <form onSubmit={handleSubmit} className="p-8 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Name</label>
                <input 
                  type="text" 
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-all" 
                  placeholder="Enter your full name"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <input 
                  type="email" 
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-all" 
                  placeholder="your@email.com"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Community Type</label>
                <select 
                  name="communityType"
                  value={formData.communityType}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-all"
                  required
                >
                  <option value="">Select community type</option>
                  <option value="professional">Professional Network</option>
                  <option value="hobby">Hobby & Interest Group</option>
                  <option value="educational">Educational Community</option>
                  <option value="local">Local Community</option>
                  <option value="business">Business & Startup</option>
                  <option value="ngo">Non-Profit Organization</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tell us about your community vision</label>
                <textarea 
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-all resize-none" 
                  placeholder="Describe what kind of community you want to build..."
                />
              </div>
              
              <button 
                type="submit" 
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-600 transition-all duration-300 hover:shadow-lg transform hover:scale-[1.02]"
              >
                Start Building My Community
              </button>
              
              <p className="text-sm text-gray-500 text-center">
                Free forever plan available. No credit card required to get started.
              </p>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
